import classNames from 'classnames';
import debounce from 'lodash/debounce';
import React, { useCallback, useRef, useState } from 'react';
import { useMemo } from 'react';
import { DateRange, rangeConfig } from 'src/components/DateRange';
import { TablePaginationBtns } from 'src/components/PaginationBtns';
import {
  Button,
  Col,
  // downloadFile,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Icon,
  Input,
  Row,
  useScreenSize
} from 'straightline-ui';
import { formatDate } from 'straightline-utils/time';

export default function SearchBar({ queryState, total, sortLabels }) {
  const { isXs, isSm, isMd, isLg, isXl } = useScreenSize();
  const rangeWrapper = useRef(null);

  const { getInState, opInState, delInState, setInState } = queryState;
  const query = getInState('query', {});
  const $search = getInState('query.$search', '');
  const [text, setText] = useState($search);

  // eslint-disable-next-line
  const debouncedHandleChange = useCallback(
    debounce((value) => {
      opInState((newState) => {
        newState.del('query.$skip');

        const val = value && value.trim ? value.trim() : value;

        if (val) {
          newState.set('query.$search', val);
        } else {
          newState.del('query.$search');
        }

        return newState;
      });
    }, 300),
    []
  );

  const handleSearchChange = ({ target: { value } }) => {
    setText(value);
    debouncedHandleChange(value);
  };

  const handleSearchClear = () => {
    setText('');
    delInState('query.$search', 'query.$skip');
  };

  const ranges = useMemo(
    () => rangeConfig(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formatDate(new Date(), 'dateOnly')]
  );

  const handleDateChange = ({ target: { value } }) => {
    opInState((newState) => {
      newState.del('query.$skip');

      if (!value || !value.length || !value[0] || !value[1]) {
        newState.del('query.pickup_date');
        return newState;
      }

      newState.set('query.pickup_date', {
        $between: [
          formatDate(value[0], 'dateOnly'),
          formatDate(value[1], 'dateOnly')
        ]
      });

      return newState;
    });
  };

  return (
    <div className="px-3" ref={rangeWrapper}>
      <Row className="align-items-center">
        <Col
          md="12"
          lg
          className={classNames('py-2', {
            'border-right': isLg || isXl,
            'border-bottom': isXs || isSm || isMd
          })}
        >
          <Row className="align-items-center">
            <Col>
              <Row className="align-items-center">
                <Col className="col-auto pr-0">
                  <Icon type="search" className="text-muted" />
                </Col>
                <Col>
                  <Input
                    value={text}
                    onChange={handleSearchChange}
                    className="form-control-flush"
                    placeholder="Search"
                  />
                </Col>
                {text && (
                  <Col className="col-auto p-0">
                    <Button
                      className="text-muted"
                      color="default"
                      type="button"
                      size="sm"
                      onClick={handleSearchClear}
                    >
                      <Icon type="x" />
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
        <Col
          md="12"
          lg="auto"
          className="py-3 py-lg-2 d-flex align-items-center"
        >
          <Row noGutters className="mb-n1">
            <Col xs="auto" className="mr-2 mb-1">
              <TablePaginationBtns
                total={total}
                limit={10}
                skip={query.$skip || 0}
                setSkip={($skip) => setInState('query.$skip', $skip)}
                color="light"
              />
            </Col>
            <Col xs="auto" className="mr-2 mb-1">
              <SortButton queryState={queryState} sortLabels={sortLabels} />
            </Col>
            <Col xs="auto" className="mb-1">
              <DateRange
                right
                placeholder="Pickup Date"
                size="sm"
                name="pickup_date"
                value={query.pickup_date?.$between || []}
                ranges={ranges}
                onChange={handleDateChange}
                wrapper={rangeWrapper}
              />
            </Col>
            {/* <Col xs="auto" className="mb-1">
              <DownloadButton query={query} size="sm" color="light">
                <Icon type="download" className="mr-1" />
                Download
              </DownloadButton>
            </Col> */}
          </Row>
        </Col>
      </Row>
    </div>
  );
}

// const DownloadButton = ({ query, ...rest }) => {
//   const [loading, setLoading] = useState(false);
//   const app = useApp();

//   const handleClick = async () => {
//     setLoading(true);
//     try {
//       const { data } = await app
//         .service('api/shipment-reports')
//         .find({ query });
//       downloadFile(data, 'Straightline Shipments', 'text/csv;charset=utf-8;');
//       setLoading(false);
//     } catch (error) {
//       setLoading(false);
//       toast.errors(error);
//     }
//   };

//   return <Button {...rest} loading={loading} onClick={handleClick} />;
// };

const SortButton = ({ queryState, sortLabels }) => {
  const { setInState, getInState } = queryState;
  const $sort = getInState('query.$sort', {});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen(!dropdownOpen);

  const handleSortChange = ($sort) => {
    setInState('query.$sort', $sort);
  };

  const [activeKey, value] =
    Object.entries(sortLabels).find(([key]) => $sort[key]) || [];

  return (
    <Dropdown isOpen={dropdownOpen} toggle={toggle}>
      <DropdownToggle size="sm" color="light">
        {(() => {
          if (activeKey) {
            return (
              <span>
                Sort: {value}
                <Icon
                  className="ml-2"
                  type={
                    Number($sort[activeKey]) === 1 ? 'arrow-up' : 'arrow-down'
                  }
                />
              </span>
            );
          }
          return <span>Sort</span>;
        })()}
      </DropdownToggle>
      <DropdownMenu right>
        {Object.entries(sortLabels).map(([key, value]) => {
          const isActive = key === activeKey;

          return (
            <div
              className="dropdown-item d-flex justify-content-between align-items-center"
              key={key}
            >
              <div className={isActive ? 'text-dark' : ''}>{value}</div>
              <div className="ml-5">
                <Button
                  size="sm"
                  color="white"
                  className="mr-2"
                  disabled={isActive && Number($sort[activeKey]) === 1}
                  onClick={() => {
                    toggle();
                    handleSortChange({ [key]: 1 });
                  }}
                >
                  <Icon type="arrow-up" />
                </Button>
                <Button
                  size="sm"
                  color="white"
                  disabled={isActive && Number($sort[activeKey]) === -1}
                  onClick={() => {
                    toggle();
                    handleSortChange({ [key]: -1 });
                  }}
                >
                  <Icon type="arrow-down" />
                </Button>
              </div>
            </div>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};
